/**
 * Auth State Caching Utility
 * ==========================
 *
 * Provides localStorage-based caching for authentication state to enable
 * instant page loads for returning users. Uses a "stale-while-revalidate"
 * pattern where cached data is shown immediately while fresh data is
 * fetched in the background.
 *
 * ## Architecture
 *
 * - **Auth Cache** (localStorage): Stores user/profile data with configurable TTL
 * - **Session Expired Flag** (sessionStorage): Tracks if session expired for banner display
 *
 * ## Security Notes
 *
 * - Cache stores derived data (user info, profile), not raw JWT tokens
 * - Supabase handles actual session/token storage separately
 * - 5-minute default TTL limits exposure window
 * - Session expired flag uses sessionStorage (cleared on tab close)
 *
 * @module authCache
 * @see docs/architecture/cache/AUTH_STATE_CACHING.md (follow-up documentation)
 */

import type { User, Session } from '@supabase/supabase-js';
import {
  STORAGE_KEYS,
  getStorageItem,
  setStorageItem,
  removeStorageItem,
} from '@/lib/storage';

// ============================================================================
// Configuration
// ============================================================================

/**
 * Auth cache configuration options.
 * Modify these values to adjust caching behavior.
 */
export const AUTH_CACHE_CONFIG = {
  /**
   * Time-to-live for cached auth state in milliseconds.
   * After this period, cache is considered stale and will be revalidated.
   * @default 300000 (5 minutes)
   */
  ttlMs: 5 * 60 * 1000,

  /**
   * Current cache schema version.
   * Increment this when the cache structure changes to invalidate old caches.
   */
  version: 1,
} as const;

// ============================================================================
// Types
// ============================================================================

/**
 * Cached user profile information.
 * Mirrors the UserProfile type from AuthContext.
 */
export interface CachedProfile {
  /** User's unique identifier */
  id: string;
  /** User's role (admin, user, etc.) */
  role: string;
  /** Organization ID (null for individual users) */
  organization_id: string | null;
  /** User's full name */
  full_name: string | null;
  /** Organization name for display */
  organizationName: string | null;
}

/**
 * Complete cached authentication state.
 * Stored in localStorage with TTL for instant page loads.
 */
export interface CachedAuthState {
  /** Supabase user object */
  user: User | null;
  /** Supabase session object (includes access_token for offline JWT parsing) */
  session: Session | null;
  /** Derived user profile from JWT claims */
  profile: CachedProfile | null;
  /** Unix timestamp (ms) when cache was created */
  cachedAt: number;
  /** Schema version for cache invalidation on structure changes */
  version: number;
}

// ============================================================================
// Constants
// ============================================================================

/** Storage key for auth cache */
const AUTH_CACHE_KEY = STORAGE_KEYS.AUTH_CACHE;

/** sessionStorage key for session expired flag */
const SESSION_EXPIRED_KEY = 'coordi_session_expired';

// ============================================================================
// Auth Cache Functions
// ============================================================================

/**
 * Retrieves cached auth state if valid (not expired and correct version).
 *
 * Use this for the fast path when you want fresh-enough data.
 *
 * @returns Cached auth state if valid, null otherwise
 *
 * @example
 * ```typescript
 * const cached = getCachedAuthState();
 * if (cached?.user) {
 *   // Use cached user data immediately - skip network call
 *   setUser(cached.user);
 *   setAuthLoading(false);
 * }
 * ```
 */
export function getCachedAuthState(): CachedAuthState | null {
  try {
    const cached = getStorageItem<string>(AUTH_CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedAuthState = JSON.parse(cached);

    // Version mismatch - invalidate cache
    if (parsed.version !== AUTH_CACHE_CONFIG.version) {
      console.debug('[AuthCache] Version mismatch, clearing cache');
      clearAuthCache();
      return null;
    }

    // Check if cache has expired
    const age = Date.now() - parsed.cachedAt;
    if (age > AUTH_CACHE_CONFIG.ttlMs) {
      console.debug('[AuthCache] Cache expired (age: %dms, ttl: %dms)', age, AUTH_CACHE_CONFIG.ttlMs);
      return null;
    }

    console.debug('[AuthCache] Valid cache found (age: %dms)', age);
    return parsed;
  } catch (e) {
    console.warn('[AuthCache] Failed to parse cache:', e);
    clearAuthCache();
    return null;
  }
}

/**
 * Retrieves cached auth state even if stale (expired but correct version).
 *
 * Used for "stale-while-revalidate" pattern where we show old data
 * while fetching fresh data in the background. This provides the best
 * user experience for returning users.
 *
 * @returns Cached auth state if exists and valid version, null otherwise
 *
 * @example
 * ```typescript
 * const stale = getStaleAuthCache();
 * if (stale?.user) {
 *   // Show stale data immediately for instant UX
 *   setUser(stale.user);
 *   setAuthLoading(false);
 *
 *   // Revalidate in background (don't await)
 *   validateSessionInBackground();
 * }
 * ```
 */
export function getStaleAuthCache(): CachedAuthState | null {
  try {
    const cached = getStorageItem<string>(AUTH_CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedAuthState = JSON.parse(cached);

    // Only check version, ignore TTL for stale-while-revalidate
    if (parsed.version !== AUTH_CACHE_CONFIG.version) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Saves auth state to cache with current timestamp.
 *
 * Call this after successful authentication or session refresh
 * to keep the cache up-to-date.
 *
 * @param user - Supabase user object (or null if logged out)
 * @param session - Supabase session object (or null if logged out)
 * @param profile - Derived user profile (or null if not loaded)
 *
 * @example
 * ```typescript
 * // After successful authentication
 * setCachedAuthState(session.user, session, {
 *   id: user.id,
 *   role: claims.role,
 *   organization_id: claims.organization_id,
 *   full_name: user.user_metadata.full_name,
 *   organizationName: claims.organization_name,
 * });
 * ```
 */
export function setCachedAuthState(
  user: User | null,
  session: Session | null,
  profile: CachedProfile | null
): void {
  const state: CachedAuthState = {
    user,
    session,
    profile,
    cachedAt: Date.now(),
    version: AUTH_CACHE_CONFIG.version,
  };

  try {
    setStorageItem(AUTH_CACHE_KEY, state);
    console.debug('[AuthCache] Cache updated');
  } catch (e) {
    console.warn('[AuthCache] Failed to save cache:', e);
  }
}

/**
 * Clears the auth cache from browser storage.
 *
 * Should be called on:
 * - Manual sign out (user intentionally logged out)
 * - Cache corruption detected
 * - Version mismatch
 *
 * @example
 * ```typescript
 * // On sign out
 * clearAuthCache();
 * await supabase.auth.signOut();
 * ```
 */
export function clearAuthCache(): void {
  try {
    removeStorageItem(AUTH_CACHE_KEY);
    console.debug('[AuthCache] Cache cleared');
  } catch (e) {
    console.warn('[AuthCache] Failed to clear cache:', e);
  }
}

/**
 * Checks if the cached state is fresh (within TTL).
 *
 * Use this to decide whether to trigger background revalidation.
 *
 * @param cached - Cached auth state to check
 * @returns true if cache is fresh, false if stale
 *
 * @example
 * ```typescript
 * const cached = getStaleAuthCache();
 * if (cached && !isAuthCacheFresh(cached)) {
 *   // Cache is stale - trigger background revalidation
 *   validateSessionInBackground();
 * }
 * ```
 */
export function isAuthCacheFresh(cached: CachedAuthState): boolean {
  return Date.now() - cached.cachedAt < AUTH_CACHE_CONFIG.ttlMs;
}

/**
 * Gets the age of the cache in milliseconds.
 *
 * Useful for debugging and monitoring cache behavior.
 *
 * @param cached - Cached auth state to check
 * @returns Age in milliseconds
 */
export function getAuthCacheAge(cached: CachedAuthState): number {
  return Date.now() - cached.cachedAt;
}

// ============================================================================
// Session Expired Flag Functions
// ============================================================================

/**
 * Marks that the session has expired.
 *
 * Uses sessionStorage so the flag is cleared when the browser tab closes.
 * This triggers the "Session Expired" banner on the login page.
 *
 * **When to call this:**
 * - Background session validation fails (401 or no session)
 * - Supabase returns a TOKEN_REFRESH_FAILED event
 * - API returns 401 Unauthorized
 *
 * **When NOT to call this:**
 * - User clicks "Sign Out" (intentional logout, not expiration)
 *
 * @example
 * ```typescript
 * // In background validation
 * const { data, error } = await supabase.auth.getSession();
 * if (error || !data.session) {
 *   markSessionExpired();
 *   clearAuthCache();
 *   navigate('/login');
 * }
 * ```
 */
export function markSessionExpired(): void {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true');
    console.debug('[AuthCache] Session marked as expired');
  } catch (e) {
    console.warn('[AuthCache] Failed to set session expired flag:', e);
  }
}

/**
 * Checks if the session was previously marked as expired.
 *
 * Used to show the "Session Expired" banner on the login page.
 * The flag persists until explicitly cleared or the tab is closed.
 *
 * @returns true if session was marked as expired, false otherwise
 *
 * @example
 * ```typescript
 * // In Login component
 * const showBanner = wasSessionExpired();
 *
 * return (
 *   <>
 *     {showBanner && <SessionExpiredBanner onDismiss={clearSessionExpiredFlag} />}
 *     <LoginForm />
 *   </>
 * );
 * ```
 */
export function wasSessionExpired(): boolean {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Clears the session expired flag.
 *
 * Call this when:
 * - User dismisses the "Session Expired" banner
 * - User successfully logs in
 *
 * @example
 * ```typescript
 * // On banner dismiss
 * const handleDismiss = () => {
 *   clearSessionExpiredFlag();
 *   setShowBanner(false);
 * };
 *
 * // On successful login
 * const handleLoginSuccess = () => {
 *   clearSessionExpiredFlag();
 *   navigate('/dashboard');
 * };
 * ```
 */
export function clearSessionExpiredFlag(): void {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    console.debug('[AuthCache] Session expired flag cleared');
  } catch (e) {
    console.warn('[AuthCache] Failed to clear session expired flag:', e);
  }
}
